import { useMemo, useRef, useState } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Graticule,
  Sphere,
  ZoomableGroup,
} from 'react-simple-maps';
import { COUNTRY_BY_NUMERIC } from '@/data/countries';
import { TIER_CONFIG, type Tier } from '@/data/tiers';
import { REGIONS } from '@/data/regions';
import { theme } from '@/analyses/_shared/theme';
import {
  SubnationalLayer,
  COUNTRIES_WITH_SUBNATIONAL,
  type SubnationalStatus,
  type HoveredSubRegion,
} from '@/components/SubnationalLayer';
import { StateHoverCard } from '@/components/StateHoverCard';

// Public topojson — Natural Earth countries at 1:50m (higher detail than 110m).
// ~400KB vs 90KB but renders small territories (Gibraltar, Malta, etc.) and much
// cleaner coastlines, especially when zoomed in. CDN-hosted; bundler-agnostic.
const GEO_URL =
  'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json';

const MIN_ZOOM = 1;
const MAX_ZOOM = 10;
const ZOOM_STEP = 1.6;
const DEFAULT_ROTATE: [number, number, number] = [-20, -10, 0];
const DEFAULT_ZOOM = 1;
// Projection scale — `geoOrthographic` renders a sphere; the ComposableMap
// projection scale × zoom sets the visible radius.
const GLOBE_SCALE = 260;

type Position = {
  rotate: [number, number, number];
  zoom: number;
};

export type ProjectionMode = 'globe' | 'flat';

export type WorldMapProps = {
  onCountryClick: (countryName: string) => void;
  onCountryHover: (countryName: string | null) => void;
  filterTier: Tier | null;
  searchQuery: string;
  highlightedIso2?: string | null;
};

export const WorldMap = ({
  onCountryClick,
  onCountryHover,
  filterTier,
  searchQuery,
  highlightedIso2,
}: WorldMapProps) => {
  const [mode, setMode] = useState<ProjectionMode>('globe');
  const [position, setPosition] = useState<Position>({
    rotate: DEFAULT_ROTATE,
    zoom: DEFAULT_ZOOM,
  });

  /** Show the admin-1 overlay (US states, ES CCAA, AU states, etc.) */
  const [showSubnational, setShowSubnational] = useState(false);
  /** Rich hover payload for the sub-national region — drives the state card. */
  const [subHover, setSubHover] = useState<HoveredSubRegion | null>(null);
  /** Loading/error flag from the sub-national admin-1 fetch. */
  const [subStatus, setSubStatus] = useState<SubnationalStatus>({
    loading: false,
    error: null,
    ready: false,
  });

  // Drag-to-rotate state (globe mode)
  const dragging = useRef<{ x: number; y: number; rotate: [number, number, number] } | null>(null);

  const normQuery = searchQuery.trim().toLowerCase();

  /** When the state overlay is showing, dim the parent country's body so the
   *  admin-1 polygons read clearly on top. The parent is still clickable. */
  const subnationalParentIsos = useMemo(
    () => new Set<string>(COUNTRIES_WITH_SUBNATIONAL),
    [],
  );

  const regionMatchedIsos = useMemo(() => {
    if (!normQuery) return new Set<string>();
    const matches = Object.values(REGIONS).filter((r) =>
      r.name.toLowerCase().includes(normQuery),
    );
    return new Set(matches.flatMap((r) => r.memberCountries));
  }, [normQuery]);

  const handleZoomIn = () => {
    setPosition((p) => ({
      ...p,
      zoom: Math.min(p.zoom * ZOOM_STEP, MAX_ZOOM),
    }));
  };

  const handleZoomOut = () => {
    setPosition((p) => ({
      ...p,
      zoom: Math.max(p.zoom / ZOOM_STEP, MIN_ZOOM),
    }));
  };

  const handleReset = () => {
    setPosition({ rotate: DEFAULT_ROTATE, zoom: DEFAULT_ZOOM });
  };

  const canZoomIn = position.zoom < MAX_ZOOM;
  const canZoomOut = position.zoom > MIN_ZOOM;
  const isDefaultView =
    position.zoom === DEFAULT_ZOOM &&
    position.rotate[0] === DEFAULT_ROTATE[0] &&
    position.rotate[1] === DEFAULT_ROTATE[1];

  /* ── Drag-to-rotate handlers (globe only) ─────────────────────── */
  const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    if (mode !== 'globe') return;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    dragging.current = {
      x: e.clientX,
      y: e.clientY,
      rotate: position.rotate,
    };
  };

  const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (mode !== 'globe' || !dragging.current) return;
    const dx = e.clientX - dragging.current.x;
    const dy = e.clientY - dragging.current.y;
    // Scale drag speed inversely with zoom — feels natural
    const speed = 0.35 / position.zoom;
    const [λ0, φ0, γ0] = dragging.current.rotate;
    const newLambda = λ0 + dx * speed;
    const newPhi = Math.max(-85, Math.min(85, φ0 - dy * speed));
    setPosition((p) => ({ ...p, rotate: [newLambda, newPhi, γ0] }));
  };

  const onPointerUp = (e: React.PointerEvent<SVGSVGElement>) => {
    if (mode !== 'globe') return;
    (e.target as Element).releasePointerCapture?.(e.pointerId);
    dragging.current = null;
  };

  const projection = mode === 'globe' ? 'geoOrthographic' : 'geoEqualEarth';
  const projectionConfig =
    mode === 'globe'
      ? { scale: GLOBE_SCALE, rotate: position.rotate }
      : { scale: 175 };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <ComposableMap
        projection={projection}
        projectionConfig={projectionConfig}
        style={{
          width: '100%',
          height: '100%',
          cursor:
            mode === 'globe'
              ? dragging.current
                ? 'grabbing'
                : 'grab'
              : position.zoom > 1
              ? 'grab'
              : 'default',
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
      >
        {mode === 'flat' ? (
          <ZoomableGroup
            zoom={position.zoom}
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            onMoveEnd={({ zoom }) =>
              setPosition((p) => ({ ...p, zoom }))
            }
          >
            {renderGeographies()}
            <SubnationalLayer
              enabled={showSubnational}
              onCountryClick={onCountryClick}
              onHover={setSubHover}
              onCountryHover={onCountryHover}
              onStatus={setSubStatus}
              highlightedIso2={highlightedIso2}
            />
          </ZoomableGroup>
        ) : (
          <>
            {/* Sphere fill — the "ocean" */}
            <Sphere
              id="globe-sphere"
              fill={theme.DARK}
              stroke={theme.BORDER}
              strokeWidth={0.6}
            />
            <Graticule
              stroke={theme.BORDER_SOFT}
              strokeWidth={0.4}
              opacity={0.55}
            />
            {renderGeographies()}
            <SubnationalLayer
              enabled={showSubnational}
              onCountryClick={onCountryClick}
              onHover={setSubHover}
              onCountryHover={onCountryHover}
              onStatus={setSubStatus}
              highlightedIso2={highlightedIso2}
            />
          </>
        )}
      </ComposableMap>

      {/* Mode toggle + sub-national overlay toggle */}
      <div
        className="no-print"
        style={{
          position: 'absolute',
          top: 20,
          left: 20,
          display: 'flex',
          gap: 10,
          zIndex: 5,
        }}
      >
        <div
          style={{
            display: 'flex',
            background: theme.DARK2,
            border: `1px solid ${theme.BORDER}`,
            borderRadius: 8,
            padding: 3,
            boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
          }}
        >
          <ModeButton
            active={mode === 'globe'}
            onClick={() => setMode('globe')}
            label="Globe"
          />
          <ModeButton
            active={mode === 'flat'}
            onClick={() => setMode('flat')}
            label="Flat"
          />
        </div>

        <button
          onClick={() => setShowSubnational((v) => !v)}
          title={
            subStatus.error
              ? `Couldn't load admin-1 data: ${subStatus.error}`
              : 'Overlay US states, Spanish CCAA, Australian states, Canadian provinces, Mexican states, Argentine provinces'
          }
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: showSubnational
              ? subStatus.error
                ? theme.RED
                : theme.BLUE
              : theme.DARK2,
            color: showSubnational ? theme.WHITE : theme.TEXT,
            border: `1px solid ${
              showSubnational
                ? subStatus.error
                  ? theme.RED
                  : theme.BLUE
                : theme.BORDER
            }`,
            borderRadius: 8,
            padding: '6px 12px',
            fontSize: 11.5,
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
            transition: 'background 0.15s, border-color 0.15s',
          }}
        >
          <span
            aria-hidden
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: showSubnational ? theme.WHITE : theme.MUTED,
              display: 'inline-block',
              animation: subStatus.loading ? 'rsm-pulse 1s infinite' : undefined,
            }}
          />
          {subStatus.loading
            ? 'Loading states…'
            : subStatus.error
              ? 'Retry states'
              : 'States & provinces'}
        </button>
        {/* inline keyframes — avoids touching global CSS */}
        <style>{`@keyframes rsm-pulse{0%{opacity:1}50%{opacity:.35}100%{opacity:1}}`}</style>
      </div>

      {/* Zoom controls */}
      <div
        className="no-print"
        style={{
          position: 'absolute',
          top: 20,
          right: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          zIndex: 5,
        }}
      >
        <ZoomButton onClick={handleZoomIn} disabled={!canZoomIn} title="Zoom in" ariaLabel="Zoom in">
          +
        </ZoomButton>
        <ZoomButton onClick={handleZoomOut} disabled={!canZoomOut} title="Zoom out" ariaLabel="Zoom out">
          −
        </ZoomButton>
        <ZoomButton onClick={handleReset} disabled={isDefaultView} title="Reset view" ariaLabel="Reset view" compact>
          ⌂
        </ZoomButton>
      </div>

      {/* Hint */}
      <div
        className="no-print"
        style={{
          position: 'absolute',
          bottom: 20,
          right: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '6px 12px',
          background: `${theme.DARK2}dd`,
          border: `1px solid ${theme.BORDER}`,
          borderRadius: 8,
          fontSize: 11,
          color: theme.MUTED,
          zIndex: 5,
          pointerEvents: 'none',
          backdropFilter: 'blur(6px)',
          maxWidth: 'calc(100% - 100px)',
        }}
      >
        <span>Zoom {position.zoom.toFixed(1)}×</span>
        <span style={{ color: theme.BORDER }}>·</span>
        <span>
          {mode === 'globe'
            ? 'Drag to rotate · scroll to zoom'
            : 'Scroll / pinch to zoom · drag to pan'}
        </span>
        {showSubnational && subHover && (
          <>
            <span style={{ color: theme.BORDER }}>·</span>
            <span
              style={{
                color: theme.TEXT,
                fontWeight: 500,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 360,
              }}
            >
              {subHover.regionName}, {subHover.country.name}
            </span>
          </>
        )}
      </div>

      {/* Floating card for the hovered state/CCAA/province — sits to the
          right of the country card so the user sees both levels at once. */}
      <StateHoverCard hovered={subHover} />
    </div>
  );

  /* ── Shared geographies renderer ───────────────────────────────── */
  function renderGeographies() {
    return (
      <Geographies geography={GEO_URL}>
        {({ geographies }) =>
          geographies.map((geo) => {
            const rawId = String(geo.id ?? '');
            const numeric = rawId.padStart(3, '0');
            const country = COUNTRY_BY_NUMERIC[numeric];
            const tier: Tier = country?.tier ?? 'not-analyzed';
            const tierColor = TIER_CONFIG[tier].color;

            const tierFilteredOut = filterTier && tier !== filterTier;

            const nameMatchesSearch =
              normQuery.length === 0
                ? true
                : (country?.name.toLowerCase().includes(normQuery) ?? false) ||
                  country?.iso2.toLowerCase() === normQuery ||
                  regionMatchedIsos.has(country?.iso2 ?? '');

            const searchDimmed = normQuery.length > 0 && !nameMatchesSearch;

            const isHighlighted = highlightedIso2 && country?.iso2 === highlightedIso2;

            const dimmed = tierFilteredOut || searchDimmed;

            // If the admin-1 overlay is showing, fade the parent country body
            // so the state polygons read clearly on top. Still clickable.
            const coveredByOverlay =
              showSubnational && country && subnationalParentIsos.has(country.iso2);

            const fillOpacity = coveredByOverlay
              ? 0.25
              : isHighlighted
                ? 0.95
                : dimmed
                  ? 0.08
                  : 0.75;

            return (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                onClick={() => country && onCountryClick(country.name)}
                onMouseEnter={() => country && onCountryHover(country.name)}
                onMouseLeave={() => onCountryHover(null)}
                style={{
                  default: {
                    fill: tierColor,
                    fillOpacity,
                    stroke: theme.DARK2,
                    strokeWidth: 0.4,
                    outline: 'none',
                    transition: 'fill-opacity 0.2s, stroke-width 0.2s',
                  },
                  hover: {
                    fill: tierColor,
                    fillOpacity: 1,
                    cursor: country ? 'pointer' : 'default',
                    stroke: theme.WHITE,
                    strokeWidth: 1.1,
                    outline: 'none',
                  },
                  pressed: {
                    outline: 'none',
                    fill: tierColor,
                    fillOpacity: 1,
                  },
                }}
              />
            );
          })
        }
      </Geographies>
    );
  }
};

/* ============================================================================
 * Mode toggle pill
 * ==========================================================================*/
type ModeButtonProps = {
  active: boolean;
  onClick: () => void;
  label: string;
};
const ModeButton = ({ active, onClick, label }: ModeButtonProps) => (
  <button
    onClick={onClick}
    style={{
      background: active ? theme.BLUE : 'transparent',
      color: active ? theme.WHITE : theme.MUTED,
      border: 'none',
      borderRadius: 6,
      padding: '5px 14px',
      fontSize: 11.5,
      fontWeight: 600,
      fontFamily: 'inherit',
      cursor: 'pointer',
      transition: '0.15s',
    }}
  >
    {label}
  </button>
);

/* ============================================================================
 * Zoom control button
 * ==========================================================================*/
type ZoomButtonProps = {
  onClick: () => void;
  disabled?: boolean;
  title: string;
  ariaLabel: string;
  compact?: boolean;
  children: React.ReactNode;
};
const ZoomButton = ({ onClick, disabled, title, ariaLabel, compact, children }: ZoomButtonProps) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={ariaLabel}
    style={{
      width: 40,
      height: 40,
      background: disabled ? `${theme.DARK2}aa` : theme.DARK2,
      color: disabled ? theme.BORDER : theme.WHITE,
      border: `1px solid ${theme.BORDER}`,
      borderRadius: 8,
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: compact ? 18 : 22,
      fontWeight: 600,
      fontFamily: 'inherit',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      lineHeight: 1,
      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      transition: 'background 0.15s, border-color 0.15s',
    }}
    onMouseEnter={(e) => {
      if (!disabled) {
        e.currentTarget.style.background = theme.CARD;
        e.currentTarget.style.borderColor = theme.BLUE;
      }
    }}
    onMouseLeave={(e) => {
      if (!disabled) {
        e.currentTarget.style.background = theme.DARK2;
        e.currentTarget.style.borderColor = theme.BORDER;
      }
    }}
  >
    {children}
  </button>
);
